import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { Activity, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function AitPortfolioOperationalMetrics({ opsMetrics, opsLoading, opsMonths, setOpsMonths, summary }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  return (
    <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <Activity size={14} className="text-blue-500" />
          Operational Metrics
        </h3>
        <div className="flex gap-1">
          {[3, 6, 12].map(m => (
            <button
              key={m}
              onClick={() => setOpsMonths(m)}
              className={`px-2 py-1 rounded text-xs ${
                opsMonths === m
                  ? 'bg-blue-600 text-white'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {m}mo
            </button>
          ))}
        </div>
      </div>
      {opsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-blue-500" size={20} />
        </div>
      ) : !opsMetrics ? (
        <div className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          No operational data available yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Turnover gauge + key metrics */}
          <div className="grid gap-4 lg:grid-cols-3">
            {/* Gauge */}
            <div className={`rounded-lg p-4 flex flex-col items-center justify-center ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
              <p className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Portfolio Turnover</p>
              <div className="relative">
                {(() => {
                  const r = 32, stroke = 6, circ = 2 * Math.PI * r;
                  const pct = Math.min(opsMetrics.turnover_rate, 50) / 50;
                  const color = opsMetrics.turnover_rate > 25 ? '#ef4444' : opsMetrics.turnover_rate > 15 ? '#f97316' : '#22c55e';
                  return (
                    <svg width={80} height={80} className="-rotate-90">
                      <circle cx={40} cy={40} r={r} fill="none" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth={stroke} />
                      <circle cx={40} cy={40} r={r} fill="none" stroke={color} strokeWidth={stroke}
                        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="round" />
                    </svg>
                  );
                })()}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-lg font-bold ${opsMetrics.turnover_rate > 25 ? 'text-red-500' : opsMetrics.turnover_rate > 15 ? 'text-orange-500' : 'text-green-500'}`}>{opsMetrics.turnover_rate}%</span>
                  <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>annualized</span>
                </div>
              </div>
              <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{opsMetrics.turnover_count} events in {opsMetrics.period_months}mo</p>
            </div>

            {/* Key metrics 2x2 */}
            <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Avg Vacancy', value: opsMetrics.avg_vacancy_days != null ? `${opsMetrics.avg_vacancy_days}d` : '—', sub: `${opsMetrics.vacancy_samples} samples`, color: opsMetrics.avg_vacancy_days > 30 ? 'text-red-500' : opsMetrics.avg_vacancy_days > 14 ? 'text-orange-500' : 'text-green-500' },
                  { label: 'Lease-Up Speed', value: opsMetrics.avg_lease_up_days != null ? `${opsMetrics.avg_lease_up_days}d` : '—', sub: `${opsMetrics.lease_up_samples} samples`, color: 'text-blue-500' },
                  { label: 'Notice Period', value: opsMetrics.avg_notice_days != null ? `${opsMetrics.avg_notice_days}d` : '—', sub: `${opsMetrics.notice_samples} samples`, color: 'text-blue-500' },
                  { label: 'Total Changes', value: opsMetrics.total_status_changes, sub: `last ${opsMetrics.period_months}mo` },
                ].map(s => (
                  <div key={s.label}>
                    <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                    <p className={`text-lg font-semibold leading-tight ${s.color || ''}`}>{s.value}</p>
                    <p className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{s.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Third column: Activity chart OR occupancy summary */}
            <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
              {Object.keys(opsMetrics.monthly_volume || {}).length > 1 ? (
                <>
                  <p className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Change Activity</p>
                  <ResponsiveContainer width="100%" height={100}>
                    <AreaChart data={Object.entries(opsMetrics.monthly_volume).map(([m, c]) => ({ month: m, count: c }))} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="portActFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" tick={{ fontSize: 9, fill: isDark ? '#6b7280' : '#9ca3af' }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1f2937' : '#fff', border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`, borderRadius: 8, fontSize: 11 }} />
                      <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="url(#portActFill)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <>
                  <p className={`text-[10px] uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Occupancy Snapshot</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Occupied', value: summary?.occupied || 0, color: 'text-green-500' },
                      { label: 'Leased', value: summary?.leased || 0, color: 'text-blue-500' },
                      { label: 'Notice', value: summary?.notice || 0, color: 'text-orange-500' },
                      { label: 'Vacant', value: summary?.vacant || 0, color: 'text-red-500' },
                    ].map(s => (
                      <div key={s.label}>
                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                        <p className={`text-lg font-semibold leading-tight ${s.color}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Turnover by property + Band turnover side by side */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Per-property turnover */}
            {opsMetrics.per_property_turnover?.length > 0 && (
              <div>
                <h4 className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Turnover by Property</h4>
                <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <table className="w-full text-xs">
                    <thead className={isDark ? 'bg-gray-750' : 'bg-gray-50'}>
                      <tr>
                        <th className={`text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Property</th>
                        <th className={`text-right px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Events</th>
                        <th className={`text-right px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rate</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {opsMetrics.per_property_turnover.slice(0, 10).map(p => (
                        <tr key={p.property_id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50" onClick={() => navigate(`/dashboard/ait/${p.property_id}`)}>
                          <td className="px-3 py-1.5 truncate max-w-[150px]">{p.property_name}</td>
                          <td className="px-3 py-1.5 text-right">{p.turnover_count}</td>
                          <td className={`px-3 py-1.5 text-right font-medium ${p.turnover_rate > 25 ? 'text-red-500' : p.turnover_rate > 15 ? 'text-orange-500' : 'text-green-500'}`}>{p.turnover_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Band turnover */}
            {Object.keys(opsMetrics.band_turnover || {}).length > 0 && (
              <div>
                <h4 className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Turnover by Designation Band</h4>
                <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <table className="w-full text-xs">
                    <thead className={isDark ? 'bg-gray-750' : 'bg-gray-50'}>
                      <tr>
                        <th className={`text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Band</th>
                        <th className={`text-right px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vacancies</th>
                        <th className={`text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}></th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {Object.entries(opsMetrics.band_turnover).map(([band, count]) => {
                        const maxCount = Math.max(1, ...Object.values(opsMetrics.band_turnover));
                        const pct = (count / maxCount * 100).toFixed(0);
                        return (
                          <tr key={band}>
                            <td className="px-3 py-1.5 font-medium">{band}% AMI</td>
                            <td className="px-3 py-1.5 text-right">{count}</td>
                            <td className="px-3 py-1.5 w-24">
                              <div className={`h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                <div className={`h-2 rounded-full ${Number(band) >= 70 ? 'bg-red-500' : Number(band) >= 50 ? 'bg-blue-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
